import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { updateContactDetails } from "../../utils/requestHandler";
import { toast } from "react-toastify";
import {
  canadaProvincesAndTerritories,
  countries,
  usStates,
} from "../../utils/helperFunctions/countries-&-states";

const UpdateContactModal = ({
  setShowContactModal,
  guest_info,
  guest_id,
  fetchBookingValidation,
}) => {
  const bookingDetails = useSelector((state) => state.bookingValidation.data);
  const [contactDetails, setContactDetails] = useState({
    first_name: guest_info.first_name,
    last_name: guest_info.last_name,
    email: guest_info.email,
    guest_dob: guest_info.dob,
    phone: guest_info.phone,
    country: guest_info.country,
    address: guest_info.address,
    city: guest_info.city,
    state: guest_info.state,
    zipcode: guest_info.zipcode,
    guest_gender: guest_info.gender,
    guest_id: guest_id,
    booking_id: bookingDetails.booking_id,
  });

  const validatePhone = (phone) => {
    // Ensure phone is exactly 10 digits
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const validateEmail = (email) => {
    // Regular expression for email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setContactDetails({
      ...contactDetails,
      [name]: value,
    });
  };

  const handleSave = () => {
    if (!validatePhone(contactDetails.phone)) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }

    if (!validateEmail(contactDetails.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    updateContactDetails(contactDetails)
      .then((result) => {
        toast.success("Contact details updated successfully.");
        setShowContactModal(false);
        fetchBookingValidation();
      })
      .catch((error) => {
        toast.error("An error occurred while updating travel documents.");
        console.log("error", error);
      });
  };

  return (
    <div>
      <div className="w-full max-w-lg mx-auto mt-10 p-6 relative bg-white shadow-md rounded-md">
        <div className="flex absolute right-2 top-2 justify-end">
          <div
            onClick={() => setShowContactModal(false)}
            className="cursor-pointer text-4xl"
          >
            &times;
          </div>
        </div>
        <div className="-m-6 p-6 mb-0 flex flex-col justify-center pb-0 bg-mv-lightblue">
          <div className="flex gap-5 text-mv-darkblue items-center">
            <h1 className="text-2xl uppercase font-bold mb-4">
              {guest_info.first_name + " " + guest_info.last_name}
            </h1>
            <p className="mb-2">D.O.B: {guest_info.dob}</p>
          </div>
          <p className="text-gray-600 mb-4">
            For name and date of birth edits, please reach out to our customer
            service team for assistance.{" "}
            <Link to="#" className="text-mv-darkblue underline">
              Contact customer service
            </Link>
          </p>
        </div>
        <h1 className="text-mv-darkblue uppercase font-semibold py-2 text-xl">
          Contact Information
        </h1>
        <form className="h-96 overflow-scroll pr-8">
          <div className="mb-4">
            <label className="block text-mv-darkblue font-semibold mb-1">
              Gender
            </label>
            <div className="flex items-center">
              <input
                type="radio"
                name="guest_gender"
                value="male"
                className="mr-2"
                disabled={guest_info.guest_gender}
                checked={contactDetails.guest_gender.toLowerCase() === "male"}
                onChange={handleChange}
              />
              <label className="mr-4">Male</label>
              <input
                type="radio"
                name="guest_gender"
                value="female"
                disabled={guest_info.guest_gender}
                className="mr-2 cursor-pointer"
                checked={contactDetails.guest_gender.toLowerCase() === "female"}
                onChange={handleChange}
              />
              <label className="mr-4">Female</label>
              <input
                type="radio"
                name="guest_gender"
                value="other"
                disabled={contactDetails.guest_gender}
                className="mr-2 cursor-pointer"
                checked={contactDetails.guest_gender.toLowerCase() === "other"}
                onChange={handleChange}
              />
              <label>Other</label>
            </div>
          </div>
          <div className="mb-4">
            <label
              className="block text-mv-darkblue font-semibold mb-1"
              htmlFor="email"
            >
              Email
            </label>
            <input
              disabled={guest_info.email}
              type="email"
              id="email"
              name="email"
              className="w-full px-3 py-2 border rounded-md"
              placeholder="name@email.com"
              value={contactDetails.email}
              onChange={handleChange}
            />
          </div>
          <div className="mb-4">
            <label
              className="block text-mv-darkblue font-semibold mb-1"
              htmlFor="phone"
            >
              Telephone
            </label>
            <input
              disabled={guest_info.phone}
              type="tel"
              id="phone"
              name="phone"
              className="w-full px-3 py-2 border rounded-md"
              placeholder="(555) 555-5555"
              value={contactDetails.phone}
              onChange={handleChange}
            />
          </div>
          <div className="mb-4">
            <label
              className="block text-mv-darkblue font-semibold mb-1"
              htmlFor="address"
            >
              Address
            </label>
            <input
              type="text"
              id="address"
              name="address"
              className="w-full px-3 py-2 border rounded-md"
              placeholder="1234 Somewhere Dr."
              value={contactDetails.address}
              onChange={handleChange}
            />
          </div>
          <div className="mb-4">
            <label
              className="block text-mv-darkblue font-semibold mb-1"
              htmlFor="country"
            >
              Country
            </label>
            {
              <select
                name="country"
                id="country"
                onChange={handleChange}
                value={contactDetails.country}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option disabled value="">
                  Select Country
                </option>
                {countries.map((country) => (
                  <option
                    key={country.countryCode}
                    value={country.country}
                    selected={contactDetails.country === country.country}
                  >
                    {country.country}
                  </option>
                ))}
              </select>
            }
          </div>
          <div className="mb-4">
            <label
              className="block text-mv-darkblue font-semibold mb-1"
              htmlFor="state"
            >
              State/Province
            </label>
            {contactDetails.country === "United States" ||
            contactDetails.country === "Canada" ? (
              <select
                name="state"
                id="state"
                value={contactDetails.state}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option disabled value="">
                  Select State
                </option>
                {contactDetails.country === "United States" &&
                  usStates.map((state) => (
                    <option
                      key={state.code}
                      value={state.name}
                      selected={contactDetails.state === state.name}
                    >
                      {state.name}
                    </option>
                  ))}
                {contactDetails.country === "Canada" &&
                  canadaProvincesAndTerritories.map((province) => (
                    <option
                      key={province.code}
                      value={province.name}
                      selected={contactDetails.state === province.name}
                    >
                      {province.name}
                    </option>
                  ))}
              </select>
            ) : (
              <input
                type="text"
                id="state"
                name="state"
                className="w-full px-3 py-2 border rounded-md"
                value={contactDetails.state || ""}
                onChange={handleChange}
              />
            )}
          </div>
          <div className="mb-4">
            <label
              className="block text-mv-darkblue font-semibold mb-1"
              htmlFor="city"
            >
              City
            </label>
            <input
              type="text"
              id="city"
              name="city"
              className="w-full px-3 py-2 border rounded-md"
              value={contactDetails.city || ""}
              onChange={handleChange}
            />
          </div>
          <div className="mb-4">
            <label
              className="block text-mv-darkblue font-semibold mb-1"
              htmlFor="zipcode"
            >
              Zip Code
            </label>
            <input
              type="number"
              id="zipcode"
              name="zipcode"
              className="w-full px-3 py-2 border rounded-md"
              value={contactDetails.zipcode}
              onChange={handleChange}
            />
          </div>
        </form>
        <div className="flex py-4 justify-end">
          <button
            onClick={handleSave}
            type="submit"
            className="bg-mv-darkblue text-white px-4 py-2 rounded-md"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateContactModal;
